import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Mail, Lock } from "lucide-react";
import { useLoginHooks } from "@/hooks/useLoginHooks";
import Modal from "@/components/Modal";
import AuthShell from "@/components/auth/AuthShell";
import {
  AUTH_FOOTER_LINK_CLASS,
  AUTH_LABEL_CLASS,
  AUTH_PRIMARY_BUTTON_CLASS,
  authInputClass,
} from "@/components/auth/authStyles";

interface LoginPageProps {
  /**
   * Which account type this page signs in. Chosen by the route, not a toggle:
   * `/login` → student, `/business/login` → business.
   */
  mode?: "student" | "business";
}

/**
 * Public login page. Student login lives at `/login`; business login at
 * `/business/login`. Admin login lives on the dedicated console page at
 * `/console/login`.
 */
const LoginPage: React.FC<LoginPageProps> = ({ mode = "student" }) => {
  const { t } = useTranslation();
  const {
    email,
    setEmail,
    password,
    setPassword,
    modalAlertVisible,
    modalAlertText,
    hideModalAlert,
    handleStudentLogin,
    handleBusinessLogin,
    loginType,
  } = useLoginHooks(mode);

  const handleSubmit = () => {
    if (loginType === "student") handleStudentLogin();
    else handleBusinessLogin();
  };

  const emailPlaceholder =
    loginType === "business" ? t("Business Email") : t("Your University Email");

  return (
    <>
      <AuthShell>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#333]">
            {t("Welcome back")}
          </h1>
          <p className="mt-1.5 text-sm text-[#333]/45">
            {t("Sign in to access your account")}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className={AUTH_LABEL_CLASS}>{t("Email")}</label>
            <div className="relative">
              <Mail
                size={18}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                className={authInputClass()}
                placeholder={emailPlaceholder}
              />
            </div>
          </div>
          <div>
            <label className={AUTH_LABEL_CLASS}>{t("Password")}</label>
            <div className="relative">
              <Lock
                size={18}
                className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                className={authInputClass()}
                placeholder="••••••••"
              />
            </div>
          </div>
          <div className="text-right">
            <Link
              to={
                loginType === "business"
                  ? "/business/forgot-password"
                  : "/forgot-password"
              }
              className="text-xs font-semibold text-[#ffa217] hover:text-[#e08f0e]"
            >
              {t("Forgot Password?")}
            </Link>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            className={AUTH_PRIMARY_BUTTON_CLASS}
          >
            {t("Login")}
          </button>
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          {t("New to Uninfo HK?")}{" "}
          <Link
            to={loginType === "business" ? "/business/signup" : "/signup"}
            className={AUTH_FOOTER_LINK_CLASS}
          >
            {t("Create account")}
          </Link>
        </div>

        {/* Back to home */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="text-xs text-gray-400 transition-colors hover:text-[#ffa217]"
          >
            {t("← Back to home")}
          </Link>
        </div>
      </AuthShell>

      <Modal visible={modalAlertVisible} onClose={hideModalAlert}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">
            {modalAlertText}
          </div>
          <button
            type="button"
            onClick={hideModalAlert}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default LoginPage;
